import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Save, Eye, EyeOff, KeyRound, Trash2, AlertCircle, CheckCircle2, Lock } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const LABELS = {
  STRIPE_API_KEY: {
    label: "Stripe Secret Key",
    hint: "Server-side key Stripe uses to create checkout sessions. Lives only on the backend. Starts with sk_test_ or sk_live_.",
    placeholder: "sk_live_… or sk_test_…",
  },
  STRIPE_PUBLISHABLE_KEY: {
    label: "Stripe Publishable Key",
    hint: "Public-facing key safe to embed in frontend code. Starts with pk_test_ or pk_live_.",
    placeholder: "pk_live_… or pk_test_…",
  },
  STRIPE_WEBHOOK_SECRET: {
    label: "Stripe Webhook Signing Secret",
    hint: "Signs incoming webhook events from Stripe. Find it on the webhook endpoint detail page in Stripe. Starts with whsec_.",
    placeholder: "whsec_…",
  },
};

const ModeBadge = ({ mode }) => {
  if (!mode) return null;
  const cls = mode === "live" ? "settings-mode-live" : mode === "test" ? "settings-mode-test" : "settings-mode-unknown";
  return <span className={`settings-mode-badge ${cls}`} data-testid={`mode-badge-${mode}`}>{mode === "live" ? "LIVE" : mode === "test" ? "TEST" : "?"}</span>;
};

const SourceBadge = ({ source }) => {
  const map = {
    db: { label: "OVERRIDDEN", cls: "settings-src-db" },
    env: { label: "USING ENV VAR", cls: "settings-src-env" },
    unset: { label: "NOT CONFIGURED", cls: "settings-src-unset" },
  };
  const s = map[source] || map.unset;
  return <span className={`settings-src-badge ${s.cls}`}>{s.label}</span>;
};

function SettingRow({ item, onSaved }) {
  const cfg = LABELS[item.name] || { label: item.name, hint: "", placeholder: "" };
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!value.trim()) {
      toast.error("Enter a value first, or use Clear to remove the override.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/settings/${item.name}`, { value: value.trim() });
      toast.success(`${cfg.label} saved`);
      setValue("");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Clear the database override for ${cfg.label}? The app will revert to the deployment environment variable (if any).`)) return;
    setBusy(true);
    try {
      await api.put(`/admin/settings/${item.name}`, { value: "" });
      toast.success(`${cfg.label} override cleared`);
      setValue("");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-row" data-testid={`setting-row-${item.name}`}>
      <div className="settings-row-head">
        <div className="settings-row-title">
          <KeyRound size={14} className="settings-row-icon" />
          <span>{cfg.label}</span>
          <ModeBadge mode={item.mode} />
          <SourceBadge source={item.source} />
        </div>
        {item.updated_at && (
          <div className="settings-row-updated">
            Last updated {new Date(item.updated_at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="settings-row-hint">{cfg.hint}</div>

      <div className="settings-row-current">
        <span className="settings-current-label">Current</span>
        <code className="settings-current-value" data-testid={`current-value-${item.name}`}>
          {item.is_set ? item.masked : "— not set —"}
        </code>
      </div>

      <div className="settings-row-input-wrap">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={cfg.placeholder}
          className="settings-input"
          autoComplete="off"
          spellCheck={false}
          data-testid={`setting-input-${item.name}`}
        />
        <button
          type="button"
          className="settings-show-btn"
          onClick={() => setShow((p) => !p)}
          aria-label={show ? "Hide value" : "Show value"}
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>

      <div className="settings-row-actions">
        <button
          className="settings-save-btn"
          onClick={save}
          disabled={busy || !value.trim()}
          data-testid={`save-${item.name}`}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {busy ? " Saving…" : " Save"}
        </button>
        {item.source === "db" && (
          <button
            className="settings-clear-btn"
            onClick={clear}
            disabled={busy}
            data-testid={`clear-${item.name}`}
          >
            <Trash2 size={13} /> Clear override
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings");
      setItems(r.data.settings || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stripeKey = items.find((i) => i.name === "STRIPE_API_KEY");
  const isLive = stripeKey?.mode === "live";
  const isTest = stripeKey?.mode === "test";

  return (
    <div className="settings-panel" data-testid="admin-settings-panel">
      <div className="settings-header">
        <div>
          <h2 className="settings-h">Stripe API Keys</h2>
          <p className="settings-sub">
            Rotate Stripe keys without redeploying. Values stored here override the deployment's environment variables. Clear an override to fall back to the env var.
          </p>
        </div>
        <button className="btn-ghost" onClick={load} disabled={loading} data-testid="refresh-settings">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {stripeKey && (
        <div className={`settings-banner ${isLive ? "settings-banner-live" : isTest ? "settings-banner-test" : "settings-banner-unset"}`} data-testid="settings-mode-banner">
          {isLive ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
          <div>
            <strong>
              {isLive && "LIVE mode — real payments enabled"}
              {isTest && "TEST mode — no real money will move"}
              {!stripeKey.is_set && "Stripe is not configured — checkout will not work"}
            </strong>
            <p>
              {isLive && "Card charges, Affirm, and Klarna will hit your Stripe account and route funds to your linked bank account."}
              {isTest && "Test cards (4242 4242 4242 4242) work. Add a live secret key below to switch to real payments."}
              {!stripeKey.is_set && "Add at least the Stripe Secret Key below to enable checkout."}
            </p>
          </div>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="settings-loading"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      ) : (
        <div className="settings-list">
          {items.map((item) => (
            <SettingRow key={item.name} item={item} onSaved={load} />
          ))}
        </div>
      )}

      <div className="settings-footnotes">
        <h4>How to switch from TEST to LIVE</h4>
        <ol>
          <li>In Stripe Dashboard, toggle <strong>Test mode OFF</strong> (top bar).</li>
          <li>Go to <strong>Developers → API keys</strong>. Reveal the live <em>Secret key</em> (<code>sk_live_…</code>) and copy it. Paste here as <strong>Stripe Secret Key</strong>.</li>
          <li>Copy the live <em>Publishable key</em> (<code>pk_live_…</code>) and paste here as <strong>Stripe Publishable Key</strong>.</li>
          <li>Go to <strong>Developers → Webhooks → Add endpoint</strong>. URL: <code>https://texasbusinessdispute.com/api/webhook/stripe</code>. Select events: <code>checkout.session.completed</code>, <code>checkout.session.expired</code>, <code>checkout.session.async_payment_succeeded</code>, <code>checkout.session.async_payment_failed</code>. Reveal the signing secret (<code>whsec_…</code>) and paste here as <strong>Webhook Signing Secret</strong>.</li>
          <li>Refresh this page — the banner above should turn green and show <strong>LIVE mode</strong>.</li>
        </ol>
        <p className="settings-foot-note">
          Values saved here are stored in the application's encrypted database and override any environment variables at runtime. They take effect immediately — no redeploy needed.
        </p>
      </div>

      <ChangePasswordSection />
    </div>
  );
}

function ChangePasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const validate = () => {
    if (!current) return "Enter your current password.";
    if (!next) return "Enter a new password.";
    if (next.length < 8) return "New password must be at least 8 characters.";
    if (next === current) return "New password must differ from current.";
    if (/^[A-Za-z]+$/.test(next)) return "New password must include a number or symbol.";
    if (next !== confirm) return "New password and confirmation do not match.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Use your new password next time you log in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-password" data-testid="change-password-section">
      <div className="settings-password-head">
        <Lock size={14} className="settings-row-icon" />
        <div>
          <h3 className="settings-password-h">Admin Password</h3>
          <p className="settings-password-sub">
            Change your login password. You'll stay logged in — the new password takes effect on your next sign-in.
          </p>
        </div>
      </div>

      <form className="settings-password-form" onSubmit={submit}>
        <label className="settings-password-label">
          <span>Current password</span>
          <div className="settings-row-input-wrap">
            <input
              type={showCurrent ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="settings-input"
              autoComplete="current-password"
              data-testid="current-password-input"
            />
            <button type="button" className="settings-show-btn" onClick={() => setShowCurrent((p) => !p)} aria-label={showCurrent ? "Hide" : "Show"}>
              {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </label>

        <label className="settings-password-label">
          <span>New password</span>
          <div className="settings-row-input-wrap">
            <input
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="settings-input"
              autoComplete="new-password"
              minLength={8}
              data-testid="new-password-input"
            />
            <button type="button" className="settings-show-btn" onClick={() => setShowNext((p) => !p)} aria-label={showNext ? "Hide" : "Show"}>
              {showNext ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <small className="settings-password-hint">At least 8 characters and must include a number or symbol.</small>
        </label>

        <label className="settings-password-label">
          <span>Confirm new password</span>
          <div className="settings-row-input-wrap">
            <input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="settings-input"
              autoComplete="new-password"
              minLength={8}
              data-testid="confirm-password-input"
            />
          </div>
        </label>

        <div className="settings-row-actions">
          <button className="settings-save-btn" type="submit" disabled={busy} data-testid="change-password-submit">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />}
            {busy ? " Updating…" : " Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}
